import { apiRoutes, getApiUrl } from '@/constants/api';
import { Notification } from '@/types/notifications';

import { clientApiFetcher } from './fetcher/client';

type NotificationPageResponse = {
  notifications: Notification[];
  nextCursor: string | null;
  hasMore: boolean;
};

const MESSAGE_NOTIFICATION_TYPES = new Set<Notification['type']>([
  'message_new',
  'message_reply',
  'mention_chat',
]);

export const getNotifications = (params?: string) =>
  clientApiFetcher<Notification[]>(
    getApiUrl(apiRoutes.notification.getAll(params)),
  );

export const markNotificationAsRead = (id: string) =>
  clientApiFetcher<Notification>(
    getApiUrl(apiRoutes.notification.markAsRead(id)),
    {
      method: 'PATCH',
    },
  );
export const markAllNotificationsAsRead = () =>
  clientApiFetcher<void>(getApiUrl(apiRoutes.notification.markAllAsRead), {
    method: 'PATCH',
  });

export const deleteNotification = (id: string) =>
  clientApiFetcher<void>(getApiUrl(apiRoutes.notification.delete(id)), {
    method: 'DELETE',
  });

export const getNotificationUnreadCount = () =>
  clientApiFetcher<{
    unreadCounts: {
      all: number;
      messages: number;
      general: number;
    };
  }>(getApiUrl(apiRoutes.notification.getUnreadCount));

export const markUnreadMessageNotificationsAsRead =
  async (): Promise<number> => {
    let cursor: string | null = null;
    let hasMore = true;
    const unreadMessageNotificationIds: string[] = [];

    while (hasMore) {
      const params = new URLSearchParams();
      params.set('limit', '100');

      if (cursor) {
        params.set('cursor', cursor);
      }

      const page = await clientApiFetcher<NotificationPageResponse>(
        getApiUrl(apiRoutes.notification.getAll(params.toString())),
      );

      for (const notification of page.notifications) {
        if (
          !notification.read &&
          MESSAGE_NOTIFICATION_TYPES.has(notification.type)
        ) {
          unreadMessageNotificationIds.push(notification._id);
        }
      }

      hasMore = page.hasMore && Boolean(page.nextCursor);
      cursor = page.nextCursor;
    }

    if (unreadMessageNotificationIds.length === 0) {
      return 0;
    }

    const results = await Promise.allSettled(
      unreadMessageNotificationIds.map((id) => markNotificationAsRead(id)),
    );

    return results.filter((result) => result.status === 'fulfilled').length;
  };
